import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";

import Tooltip from "@material-ui/core/Tooltip";
import Icon from "@material-ui/core/Icon";
import Divider from "@material-ui/core/Divider";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import TimetableGrid from "components/Timetable/TimetableGrid.jsx";

import api from "api/index";

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const enrollmentColumns = [
  {
    id: "code",
    header: "Course Code",
    meta: { variant: "mono" },
    cell: info => <span style={{ fontWeight: 700, color: "#1a237e" }}>{info.row.original.course?.code || "—"}</span>
  },
  { id: "name", header: "Course Name", accessorFn: e => e.course?.name || "—" },
  { id: "credits", header: "Credits", accessorFn: e => e.course?.creditUnits || "—" },
  {
    id: "status",
    header: "Status",
    cell: info => {
      const status = info.row.original.status;
      return <Pill tone={status === "active" ? "success" : "muted"}>{status}</Pill>;
    }
  }
];

const resultColumns = [
  { id: "course", header: "Course", accessorFn: r => r.enrollment?.course?.name || "—" },
  { id: "cw", header: "CW", accessorFn: r => (r.courseworkScore ? `${r.courseworkScore}%` : "—") },
  { id: "exam", header: "Exam", accessorFn: r => (r.examScore ? `${r.examScore}%` : "—") },
  {
    id: "total",
    header: "Total",
    cell: info => <span style={{ fontWeight: 700 }}>{info.row.original.totalScore ? `${info.row.original.totalScore}%` : "—"}</span>
  },
  { id: "grade", header: "Grade", cell: info => <Pill tone="info">{info.row.original.grade || "—"}</Pill> },
  {
    id: "status",
    header: "Status",
    cell: info => {
      const status = info.row.original.status || "pending";
      return <Pill tone={status === "final" ? "success" : "warning"}>{status}</Pill>;
    }
  }
];

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "260px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    alignItems: "flex-end",
    color: "#fff",
    gap: "32px"
  },
  heroAvatar: {
    width: "120px",
    height: "120px",
    borderRadius: "24px",
    background: "rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "48px",
    fontWeight: 800,
    color: "#fff",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
  },
  heroInfo: { flex: 1, paddingBottom: "10px" },
  heroTitle: { fontSize: "36px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px", textShadow: "0 2px 10px rgba(0,0,0,0.2)" },
  heroSub: { fontSize: "16px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400, display: "flex", alignItems: "center", gap: "12px" },
  heroActions: { display: "flex", gap: "12px", paddingBottom: "10px" },

  contentCard: {
    borderRadius: "20px",
    boxShadow: "0 10px 30px 0 rgba(0,0,0,0.05)",
    border: "1px solid #f1f5f9",
    overflow: "hidden"
  },
  tabHeader: {
    borderBottom: "1px solid #f1f5f9",
    background: "#fff",
    padding: "0 20px"
  },
  tabLabel: {
    fontWeight: 600,
    fontSize: "13px",
    textTransform: "none",
    minWidth: "120px"
  },

  sectionTitle: {
    color: "#1e293b",
    fontWeight: 700,
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "20px",
    marginTop: "10px"
  },
  fieldGroup: {
    background: "#f8fafc",
    padding: "20px",
    borderRadius: "16px",
    border: "1px solid #f1f5f9",
    height: "100%"
  },
  field: { marginBottom: "16px", "&:last-child": { marginBottom: 0 } },
  label: { fontWeight: 600, color: "#64748b", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" },
  value: { color: "#1e293b", fontSize: "14px", fontWeight: 500 },

  // Fee Stats
  feeStat: {
    padding: "24px",
    borderRadius: "16px",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    position: "relative",
    overflow: "hidden"
  },
  feeLabel: { fontSize: "13px", fontWeight: 600, color: "#64748b" },
  feeAmount: { fontSize: "22px", fontWeight: 800, color: "#1e293b", margin: 0 },
  feeIcon: { position: "absolute", right: "-10px", bottom: "-10px", fontSize: "80px", opacity: 0.05 },

  statusChip: {
    fontWeight: 700,
    fontSize: "11px",
    borderRadius: "8px",
    height: "26px",
    padding: "0 4px"
  }
};

class StudentProfile extends Component {
  state = {
    student: null, loading: true, error: null, tab: 0,
    semesters: [], selectedSemester: "", timetable: [], loadingTimetable: false,
    portalDialogOpen: false, portalForm: { email: "", password: "" }, portalSaving: false, portalError: null
  };

  componentDidMount() {
    this.fetchStudent();
    this.fetchSemesters();
  }

  fetchStudent = async () => {
    const { id } = this.props.match.params;
    try {
      const res = await api.get(`/students/${id}`);
      this.setState({ student: res.data.data || res.data, loading: false });
    } catch (e) {
      this.setState({ error: "Student not found.", loading: false });
    }
  };

  fetchSemesters = async () => {
    try {
      const res = await api.get("/semesters");
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : d.data || [];
      const current = list.find(s => s.isCurrent) || list[0];
      this.setState({ semesters: list, selectedSemester: current ? String(current.id) : "" });
    } catch (e) { /* silent */ }
  };

  fetchTimetable = async () => {
    const { selectedSemester } = this.state;
    const { id } = this.props.match.params;
    if (!selectedSemester) return;
    this.setState({ loadingTimetable: true });
    try {
      const res = await api.get(`/timetable/student/${id}`, { params: { semesterId: selectedSemester } });
      const d = res.data.data || res.data;
      this.setState({ timetable: Array.isArray(d) ? d : d.data || [], loadingTimetable: false });
    } catch (e) {
      this.setState({ timetable: [], loadingTimetable: false });
    }
  };

  handleTabChange = (e, v) => {
    this.setState({ tab: v });
    if (v === 4) this.fetchTimetable();
  };

  handleTimetableSemesterChange = e => {
    this.setState({ selectedSemester: e.target.value }, this.fetchTimetable);
  };

  openPortalDialog = () => {
    const { student } = this.state;
    this.setState({
      portalDialogOpen: true,
      portalError: null,
      portalForm: { email: student.email || "", password: genPassword() }
    });
  };

  handlePortalFormChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ portalForm: { ...prev.portalForm, [field]: v } }));
  };

  handleCreatePortalLogin = async () => {
    const { student, portalForm } = this.state;
    if (!portalForm.email.trim() || !portalForm.password.trim()) {
      this.setState({ portalError: "Email and a temporary password are required." });
      return;
    }
    this.setState({ portalSaving: true, portalError: null });
    try {
      await api.post("/auth/users", {
        name: `${student.firstName} ${student.lastName}`.trim(),
        email: portalForm.email.trim(),
        role: "Student",
        studentId: student.id,
        password: portalForm.password.trim()
      });
      this.setState({ portalDialogOpen: false, portalSaving: false });
      this.fetchStudent();
    } catch (e) {
      this.setState({
        portalError: e.response?.data?.message || "Failed to create portal login.",
        portalSaving: false
      });
    }
  };

  InfoField = ({ label, value }) => {
    const { classes } = this.props;
    return (
      <div className={classes.field}>
        <div className={classes.label}>{label}</div>
        <div className={classes.value}>{value || "—"}</div>
      </div>
    );
  };

  render() {
    const { classes, history } = this.props;
    const {
      student, loading, error, tab, semesters, selectedSemester, timetable, loadingTimetable,
      portalDialogOpen, portalForm, portalSaving, portalError
    } = this.state;
    const { InfoField } = this;

    if (loading) return <div style={{ textAlign: "center", padding: "60px" }}><CircularProgress /></div>;
    if (error) return <p style={{ color: "#f44336", padding: "20px" }}>{error}</p>;

    const initials = `${(student.firstName || "")[0] || ""}${(student.lastName || "")[0] || ""}`.toUpperCase();
    const statusColor = { active: "#e8f5e9", deferred: "#fff8e1", withdrawn: "#ffebee", completed: "#e3f2fd" };
    const statusText = { active: "#2e7d32", deferred: "#e65100", withdrawn: "#b71c1c", completed: "#0d47a1" };

    return (
      <GridContainer>
        {/* Profile Hero Section */}
        <GridItem xs={12}>
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <div className={classes.heroAvatar}>{initials}</div>
              <div className={classes.heroInfo}>
                <h1 className={classes.heroTitle}>{student.firstName} {student.lastName}</h1>
                <div className={classes.heroSub}>
                  <Chip
                    label={student.status?.toUpperCase()}
                    className={classes.statusChip}
                    style={{
                      background: statusColor[student.status] || "rgba(255,255,255,0.2)",
                      color: statusText[student.status] || "#fff",
                      border: "none"
                    }}
                  />
                  <span>{student.studentNo}</span>
                  <Divider orientation="vertical" flexItem style={{ backgroundColor: "rgba(255,255,255,0.3)", height: "16px", alignSelf: "center" }} />
                  <span>{student.programme?.name || "Unassigned"}</span>
                  <Divider orientation="vertical" flexItem style={{ backgroundColor: "rgba(255,255,255,0.3)", height: "16px", alignSelf: "center" }} />
                  <span>Year {student.yearOfStudy}</span>
                </div>
              </div>
              <div className={classes.heroActions}>
                {student.portalAccount ? (
                  <Tooltip title={`Portal login: ${student.portalAccount.email} (${student.portalAccount.status || "active"})`}>
                    <Chip
                      icon={<Icon style={{ color: "#fff !important" }}>verified_user</Icon>}
                      label="Portal Account Active"
                      style={{ background: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700 }}
                    />
                  </Tooltip>
                ) : (
                  <Button color="white" onClick={this.openPortalDialog}>
                    <Icon style={{ marginRight: "8px" }}>vpn_key</Icon> Create Portal Login
                  </Button>
                )}
                <Button color="white" onClick={() => history.push(`/bwillis/students/${student.id}/edit`)}>
                  <Icon style={{ marginRight: "8px" }}>edit</Icon> Edit Profile
                </Button>
                <Tooltip title="Go Back">
                  <Button color="transparent" justIcon onClick={() => history.goBack()} style={{ color: "#fff" }}>
                    <Icon>arrow_back</Icon>
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </GridItem>

        {/* Main Content Tabs */}
        <GridItem xs={12}>
          <Card className={classes.contentCard}>
            <div className={classes.tabHeader}>
              <Tabs
                value={tab}
                onChange={this.handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Personal Details" className={classes.tabLabel} icon={<Icon style={{ fontSize: "20px" }}>person</Icon>} iconPosition="start" />
                <Tab label="Academic Record" className={classes.tabLabel} icon={<Icon style={{ fontSize: "20px" }}>school</Icon>} iconPosition="start" />
                <Tab label="Financials" className={classes.tabLabel} icon={<Icon style={{ fontSize: "20px" }}>account_balance_wallet</Icon>} iconPosition="start" />
                <Tab label="Results" className={classes.tabLabel} icon={<Icon style={{ fontSize: "20px" }}>assessment</Icon>} iconPosition="start" />
                <Tab label="Timetable" className={classes.tabLabel} icon={<Icon style={{ fontSize: "20px" }}>event_note</Icon>} iconPosition="start" />
              </Tabs>
            </div>
            <CardBody style={{ padding: "30px" }}>
              {tab === 0 && (
                <GridContainer>
                  <GridItem xs={12} md={6}>
                    <div className={classes.fieldGroup}>
                      <h4 className={classes.sectionTitle}><Icon>badge</Icon> Identity Info</h4>
                      <InfoField label="First Name" value={student.firstName} />
                      <InfoField label="Last Name" value={student.lastName} />
                      <InfoField label="Date of Birth" value={student.dob} />
                      <InfoField label="Gender" value={student.gender} />
                      <InfoField label="Nationality" value={student.nationality} />
                    </div>
                  </GridItem>
                  <GridItem xs={12} md={6}>
                    <div className={classes.fieldGroup}>
                      <h4 className={classes.sectionTitle}><Icon>contact_mail</Icon> Contact & Enrollment</h4>
                      <InfoField label="Email Address" value={student.email} />
                      <InfoField label="Phone Number" value={student.phone} />
                      <Divider style={{ margin: "20px 0", background: "#f1f5f9" }} />
                      <InfoField label="Enrollment Date" value={student.enrollmentDate} />
                      <InfoField label="Current Status" value={student.status} />
                    </div>
                  </GridItem>
                </GridContainer>
              )}

              {tab === 1 && (
                <div>
                  <h4 className={classes.sectionTitle}><Icon>menu_book</Icon> Enrolled Courses</h4>
                  <DataTable
                    columns={enrollmentColumns}
                    data={student.enrollments || []}
                    emptyState={
                      <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                        <Icon style={{ fontSize: "48px", opacity: 0.2 }}>history_edu</Icon>
                        <p>No enrollments found for the current semester.</p>
                      </div>
                    }
                  />
                </div>
              )}

              {tab === 2 && (
                <div>
                  <h4 className={classes.sectionTitle}><Icon>payments</Icon> Financial Summary</h4>
                  <GridContainer>
                    <GridItem xs={12} sm={4}>
                      <div className={classes.feeStat} style={{ background: "#f0f7ff" }}>
                        <span className={classes.feeLabel}>Total Invoiced</span>
                        <h3 className={classes.feeAmount}>UGX {(student.feesSummary?.totalInvoiced || 0).toLocaleString()}</h3>
                        <Icon className={classes.feeIcon}>receipt_long</Icon>
                      </div>
                    </GridItem>
                    <GridItem xs={12} sm={4}>
                      <div className={classes.feeStat} style={{ background: "#f0fdf4" }}>
                        <span className={classes.feeLabel} style={{ color: "#166534" }}>Total Paid</span>
                        <h3 className={classes.feeAmount} style={{ color: "#15803d" }}>UGX {(student.feesSummary?.totalPaid || 0).toLocaleString()}</h3>
                        <Icon className={classes.feeIcon} style={{ color: "#15803d" }}>check_circle</Icon>
                      </div>
                    </GridItem>
                    <GridItem xs={12} sm={4}>
                      <div className={classes.feeStat} style={{ background: (student.feesSummary?.balance || 0) > 0 ? "#fff1f2" : "#f0fdf4" }}>
                        <span className={classes.feeLabel} style={{ color: (student.feesSummary?.balance || 0) > 0 ? "#991b1b" : "#166534" }}>Balance Due</span>
                        <h3 className={classes.feeAmount} style={{ color: (student.feesSummary?.balance || 0) > 0 ? "#b91c1c" : "#15803d" }}>
                          UGX {(student.feesSummary?.balance || 0).toLocaleString()}
                        </h3>
                        <Icon className={classes.feeIcon} style={{ color: (student.feesSummary?.balance || 0) > 0 ? "#b91c1c" : "#15803d" }}>pending_actions</Icon>
                      </div>
                    </GridItem>
                  </GridContainer>
                </div>
              )}

              {tab === 3 && (
                <div>
                  <h4 className={classes.sectionTitle}><Icon>grade</Icon> Academic Performance</h4>
                  <DataTable
                    columns={resultColumns}
                    data={student.results || []}
                    emptyState={
                      <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                        <Icon style={{ fontSize: "48px", opacity: 0.2 }}>analytics</Icon>
                        <p>No results available for this student.</p>
                      </div>
                    }
                  />
                </div>
              )}

              {tab === 4 && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <h4 className={classes.sectionTitle} style={{ margin: 0 }}><Icon>event</Icon> Student Timetable</h4>
                    <select
                      style={{ padding: "10px 16px", border: "1px solid #f1f5f9", borderRadius: "12px", fontSize: "14px", minWidth: "220px", background: "#f8fafc", fontWeight: 600, color: "#1e293b", outline: "none" }}
                      value={selectedSemester}
                      onChange={this.handleTimetableSemesterChange}
                    >
                      <option value="">— Select Semester —</option>
                      {semesters.map(s => (
                        <option key={s.id} value={String(s.id)}>{s.name || `Semester ${s.id}`}</option>
                      ))}
                    </select>
                  </div>

                  {!selectedSemester ? (
                    <div style={{ textAlign: "center", padding: "60px", background: "#f8fafc", borderRadius: "20px", color: "#64748b" }}>
                      <Icon style={{ fontSize: "48px", marginBottom: "12px", opacity: 0.3 }}>calendar_today</Icon>
                      <p>Please select a semester to view the timetable.</p>
                    </div>
                  ) : loadingTimetable ? (
                    <div style={{ textAlign: "center", padding: "60px" }}><CircularProgress /></div>
                  ) : (
                    <TimetableGrid
                      entries={timetable}
                      emptyMessage="No timetable entries for this student's enrolled courses this semester."
                    />
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>

        {/* Create Portal Login Dialog */}
        <Dialog open={portalDialogOpen} onClose={() => this.setState({ portalDialogOpen: false })} maxWidth="xs" fullWidth>
          <DialogHeader
            title="Create Portal Login"
            subtitle={`Provision a student portal account for ${student.firstName} ${student.lastName}.`}
            icon="vpn_key"
            color="info"
            onClose={() => this.setState({ portalDialogOpen: false })}
          />
          <DialogContent style={{ paddingTop: "20px" }}>
            {portalError && <p style={{ color: "#f44336", fontSize: "13px", marginBottom: "15px" }}>{portalError}</p>}
            <CustomInput
              labelText="Login Email"
              id="portalLoginEmail"
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: portalForm.email,
                onChange: this.handlePortalFormChange("email"),
                type: "email",
                placeholder: "student@bwptc.ac.ug"
              }}
            />
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
              <CustomInput
                labelText="Temporary Password"
                id="portalLoginPassword"
                formControlProps={{ fullWidth: true }}
                inputProps={{
                  value: portalForm.password,
                  onChange: this.handlePortalFormChange("password")
                }}
                helpText="Emailed to the student. They must change it on first login."
              />
              <Button
                size="sm"
                simple
                color="info"
                onClick={() => this.setState(prev => ({ portalForm: { ...prev.portalForm, password: genPassword() } }))}
              >
                Regenerate
              </Button>
            </div>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ portalDialogOpen: false })} disabled={portalSaving}>Cancel</Button>
            <Button color="primary" onClick={this.handleCreatePortalLogin} disabled={portalSaving} style={{ fontWeight: "700" }}>
              {portalSaving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Create Login"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

StudentProfile.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired, match: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(StudentProfile));
